import React, { useMemo, useState } from "react";
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormSelect,
  CRow,
} from "@coreui/react";
import { type ApiJob, JOB_STATUS_NAMES, type JobStatusName } from "@tradingdroid/frontends-types";

import { useJobChildren } from "@src/hooks/useJobChildren";
import { getCoordinatorProgress } from "@src/utils/jobChildren";
import { JobsListTable } from "@src/pages/JobsListPage/JobsListTable";
import { PAGINATION_STALLED_MESSAGE } from "@src/utils/constants";

interface Props {
  job: ApiJob;
}

/** Child jobs a coordinator spawned, filterable by status and paged on demand. */
export const JobChildrenPanel: React.FC<Props> = ({ job }) => {
  const [status, setStatus] = useState<JobStatusName | "">("");

  const queryFilter = status ? `status_name.EQ:'${status}'` : undefined;
  const { children, loading, error, hasMore, stalled, loadMore } = useJobChildren(
    job.id,
    queryFilter,
  );

  const progress = useMemo(() => getCoordinatorProgress(job), [job]);

  return (
    <CCard className="JobChildrenPanel mb-4">
      <CCardHeader className="JobChildrenPanelHeader">
        <CRow className="align-items-center">
          <CCol>
            <h5 className="mb-0">
              Children{" "}
              {progress && (
                <span className="text-body-secondary fw-normal fs-sm">
                  {progress.done}/{progress.total} {progress.unitLabel} finished
                  {progress.failed > 0 && `, ${progress.failed} failed`}
                </span>
              )}
              {/* The ratio can reach its total on a run whose aggregate covers fewer units: a Monte
                  Carlo child that finishes without contributing a usable value is still a completed
                  child. This badge is the run's own statement that the numbers are short. */}
              {progress?.partial && (
                <CBadge
                  className="JobChildrenPartialBadge ms-2"
                  color="warning"
                  // Both, never one or the other: the reason is a raw token — `children_lost:5`, or
                  // `process_checkpoint` on the reap path — so on its own it explains nothing, and
                  // the sentence on its own drops the only detail the payload gave.
                  title={
                    "This run covers less than the planned experiment." +
                    (progress.stopReason ? ` (${progress.stopReason})` : "")
                  }
                >
                  Partial
                </CBadge>
              )}
            </h5>
          </CCol>
          <CCol xs="auto">
            <div className="JobChildrenFilter d-flex align-items-center gap-2">
              <label htmlFor="childStatusFilter" className="mb-0">
                Status:
              </label>
              <CFormSelect
                id="childStatusFilter"
                size="sm"
                value={status}
                onChange={(e) => setStatus(e.target.value as JobStatusName | "")}
              >
                <option value="">All</option>
                {JOB_STATUS_NAMES.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </CFormSelect>
            </div>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody className="JobChildrenPanelBody p-0">
        {error ? (
          <CAlert color="warning" className="m-3">
            Could not load children: {error}
          </CAlert>
        ) : (
          <>
            {stalled && (
              <CAlert color="warning" className="m-3">
                {PAGINATION_STALLED_MESSAGE}
              </CAlert>
            )}
            <JobsListTable jobs={children} loading={loading} />
            {!loading && children.length === 0 && (
              <div className="p-3 text-body-secondary">
                {status ? `No ${status} children.` : "No children yet."}
              </div>
            )}
            {hasMore && (
              <div className="p-3">
                <CButton
                  color="secondary"
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={loading}
                >
                  Load more
                </CButton>
              </div>
            )}
          </>
        )}
      </CCardBody>
    </CCard>
  );
};
